/**
 * Which language of an HQ blog post a reader is shown (contract section 5).
 *
 * A post is written in one to three languages, each complete (title AND body, enforced by
 * the server). The reader sees ONE of them: the first of their chain that the post has,
 * and every field (title, excerpt, tag, body) is read in that language. Picking each
 * field on its own would put an English tag over a French title.
 *
 * The chains are the backend's `schemas.READER_CHAINS`; the crawler pages
 * (`modules/hq_blog/share.py`) use them too, so the head a crawler reads and the page a
 * browser draws agree. A test on each side holds the two copies equal.
 */

import { isBlank } from "./blogBlank";

/** The reader's own language, then English (the reference language), then the other. */
export const BLOG_READER_CHAINS = {
  fr: ["fr", "en", "es"],
  en: ["en", "fr", "es"],
  es: ["es", "en", "fr"],
};

const filled = (v) => !isBlank(v);

const complete = (post) =>
  ["fr", "en", "es"].filter((l) => filled(post[`title_${l}`]) && filled(post[`body_${l}`]));

/**
 * @param {object} post a post as the API returns it (carries `languages` on every read).
 * @param {string} reader the reader's UI language; anything but fr/en/es reads as English.
 * @returns {"fr"|"en"|"es"} the first of the reader's chain that the post has; the chain's
 *   first language when the post has none (a legacy row), so callers always get a suffix.
 */
export function postDisplayLanguage(post, reader) {
  const chain = BLOG_READER_CHAINS[reader] || BLOG_READER_CHAINS.en;
  const available = Array.isArray(post?.languages) ? post.languages : complete(post || {});
  return chain.find((l) => available.includes(l)) || chain[0];
}

/**
 * The post as ONE reader sees it.
 *
 * @param {object | null} post
 * @param {string} reader the reader's UI language.
 * @returns {{lang: string, title: string, excerpt: string, tag: string, body: string}}
 */
export function postView(post, reader) {
  const lang = postDisplayLanguage(post, reader);
  const read = (field) => (post && post[`${field}_${lang}`]) || "";
  return {
    lang, title: read("title"), excerpt: read("excerpt"), tag: read("tag"), body: read("body"),
  };
}
